import { useQuery } from "@tanstack/react-query";
import { liveavatar } from "./client";

export type PickerOption = {
  id: string;
  label: string;
  sublabel?: string;
  preview_url?: string;
  external_voice_id?: string;
  provider?: string;
  gender?: string;
  language?: string;
  accent?: string;
  age?: string;
  description?: string;
  use_case?: string;
  default_voice_id?: string;
  category?: AvatarCategory;
  source?: "public" | "private" | "third_party";
  /** Avatar owned by the LiveAvatar account rather than the public catalogue. */
  is_user_avatar?: boolean;
};

const STALE = 5 * 60_000;

export const AVATAR_CATEGORIES = [
  "Doctor",
  "Lawyer",
  "Teacher",
  "Therapist",
  "HR",
] as const;
export type AvatarCategory = typeof AVATAR_CATEGORIES[number];

function inferAvatarCategory(name: string): AvatarCategory | undefined {
  const l = name.toLowerCase();
  if (l.includes("doctor") || l.includes("medical") || l.includes("nurse")) return "Doctor";
  if (l.includes("lawyer") || l.includes("legal")) return "Lawyer";
  if (l.includes("teacher") || l.includes("tutor") || l.includes("professor")) return "Teacher";
  if (l.includes("therapist") || l.includes("counselor")) return "Therapist";
  if (l.includes("hr") || l.includes("recruiter")) return "HR";
  return undefined;
}

// Avatars hidden from the picker (VCP-24452 #14). Filtered by id so renamed
// duplicates don't slip back in.
const HIDDEN_AVATAR_IDS = new Set<string>([
  "91342979-4c4c-44f1-bd3b-1c846d20341e", // Anthony Sitting
  "38ad67ed-98f0-407c-a2d2-4f0998b306fc", // Anthony in Black Suit
  "509609b9-cda3-4f74-b1b2-97b4d98834fd", // Anthony in White Suit
  "064703ad-91f4-4e50-866d-e251d0762c9f", // Anthony Sitting (Portrait)
  "3466af9c-45f5-40f2-8392-c51f8385422c", // Anthony in Black Suit (Portrait)
  "b26c00a5-8861-4fd3-90cc-144791d2d81d", // Anthony in White Suit (Portrait)
]);

function pick<T = unknown>(o: any, keys: string[]): T | undefined {
  for (const k of keys) {
    const v = o?.[k];
    if (v != null && v !== "") return v as T;
  }
  return undefined;
}

function mapAvatar(a: any): PickerOption | null {
  const id = pick<string>(a, ["avatar_id", "id", "uuid"]);
  if (!id) return null;
  const gender = pick<string>(a, ["gender"]);
  // The API returns default_voice as either a string id or an object
  // `{ id, name }` — normalize both shapes.
  const rawDefaultVoice = a?.default_voice;
  const defaultVoiceId =
    pick<string>(a, ["default_voice_id", "voice_id"]) ??
    (typeof rawDefaultVoice === "string"
      ? rawDefaultVoice
      : rawDefaultVoice?.id);
  return {
    id,
    label: pick<string>(a, ["name", "avatar_name", "display_name"]) ?? id,
    sublabel: [gender, pick<string>(a, ["style"])]
      .filter(Boolean).join(" · ") || undefined,
    preview_url: pick<string>(a, ["thumbnail_url", "preview_image_url", "preview_url", "image_url"]),
    gender: gender?.toLowerCase(),
    default_voice_id: defaultVoiceId,
    category: inferAvatarCategory(pick<string>(a, ["name", "avatar_name", "display_name"]) ?? ""),
  };
}

export function mapVoice(v: any): PickerOption | null {
  const id = pick<string>(v, ["voice_id", "id", "liveavatar_voice_id"]);
  if (!id) return null;
  const externalId = pick<string>(v, ["external_voice_id", "third_party_voice_id", "provider_voice_id"]);
  const provider = pick<string>(v, ["provider", "voice_provider", "source"]);
  const voiceType = pick<string>(v, ["voice_type"]);
  const isThirdParty =
    !!externalId ||
    (typeof provider === "string" && provider.toLowerCase().includes("eleven")) ||
    (typeof voiceType === "string" && voiceType.toLowerCase().includes("third"));
  const name = pick<string>(v, ["display_name", "name", "voice_name"]) ?? id;
  const metadata = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const value = v?.[key];
      if (typeof value === "string" && value.trim() && !["unknown", "n/a", "null"].includes(value.trim().toLowerCase())) return value.trim();
    }
    return undefined;
  };
  const gender = metadata(["el_gender", "gender"]);
  const language = metadata(["el_language", "language", "language_code", "locale"]);
  const accent = metadata(["el_accent", "accent"]);
  const age = metadata(["el_age", "age"]);
  const useCase = metadata(["el_use_case", "use_case"]);
  const descriptive = metadata(["el_description"]);
  // Shown: gender · "Language - accent". Age, description and use case stay on the option for future display.
  const languageLabel = language ? languageName(language.split(/[-_]/)[0]) ?? language : undefined;
  const place = languageLabel && accent ? `${languageLabel} - ${accent}` : languageLabel ?? accent;
  const sublabel = [gender, place].filter(Boolean).map((s) => String(s).replace(/_/g, " ")).join(" · ") || undefined;
  return {
    id,
    label: name,
    sublabel,
    preview_url: pick<string>(v, ["preview_url", "sample_url"]),
    external_voice_id: externalId,
    provider: isThirdParty ? "elevenlabs" : "liveavatar",
    gender: gender?.toLowerCase(),
    language,
    accent,
    age,
    description: descriptive,
    use_case: useCase,
  };
}

let langDisplay: Intl.DisplayNames | undefined;
export function languageName(code: string): string | undefined {
  if (typeof Intl === "undefined" || !Intl.DisplayNames) return undefined;
  try {
    if (!langDisplay) {
      const locale = (typeof navigator !== "undefined" && navigator.language) || "en";
      langDisplay = new Intl.DisplayNames([locale], { type: "language" });
    }
    const name = langDisplay.of(code);
    return name && name !== code ? name : undefined;
  } catch {
    return undefined;
  }
}

function mapLanguage(l: any): PickerOption | null {
  if (typeof l === "string") {
    const name = languageName(l);
    return { id: l, label: name ?? l };
  }
  const id = pick<string>(l, ["code", "language", "language_code", "id"]);
  if (!id) return null;
  const explicitName = pick<string>(l, ["name", "label", "display_name"]);
  const name = explicitName ?? languageName(id) ?? id;
  return { id, label: name };
}

/**
 * Avatar catalogue for the picker. `includeUserAvatars` additionally pulls the
 * LiveAvatar account's own custom avatars (org-gated — see
 * `canUseUserAvatars`); they are listed first and win over a public entry with
 * the same id.
 */
export function useAvatarOptions(args: { includeUserAvatars?: boolean } = {}) {
  const includeUser = !!args.includeUserAvatars;
  const q = useQuery({
    queryKey: ["liveavatar", "avatars", includeUser ? "with-user" : "public-only"],
    queryFn: async () => {
      const [pub, user] = await Promise.all([
        liveavatar.listAvatars(),
        includeUser ? liveavatar.listUserAvatars().catch(() => ({ avatars: [] })) : Promise.resolve({ avatars: [] }),
      ]);
      return { public: (pub.avatars ?? []) as any[], user: (user.avatars ?? []) as any[] };
    },
    staleTime: STALE,
  });
  const map = (list: any[], isUser: boolean) =>
    (list.map(mapAvatar).filter(Boolean) as PickerOption[])
      .filter((o) => !HIDDEN_AVATAR_IDS.has(o.id))
      .map((o) => (isUser ? { ...o, is_user_avatar: true, sublabel: o.sublabel || "Custom" } : o));

  const byId = new Map<string, PickerOption>();
  for (const o of map(q.data?.user ?? [], true)) byId.set(o.id, o);
  for (const o of map(q.data?.public ?? [], false)) if (!byId.has(o.id)) byId.set(o.id, o);
  const options = Array.from(byId.values());
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

export function useLanguageOptions() {
  const q = useQuery({
    queryKey: ["liveavatar", "languages"],
    queryFn: () => liveavatar.listLanguages(),
    staleTime: STALE,
  });
  const options = ((q.data?.languages ?? []) as any[]).map(mapLanguage).filter(Boolean) as PickerOption[];
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

export function useVoiceOptions() {
  const q = useQuery({
    queryKey: ["liveavatar", "voices"],
    queryFn: async () => {
      const [pub, priv, third] = await Promise.all([
        liveavatar.listPublicVoices().catch(() => ({ voices: [] })),
        liveavatar.listPrivateVoices().catch(() => ({ voices: [] })),
        liveavatar.listThirdPartyVoices().catch(() => ({ voices: [] })),
      ]);
      return {
        public: (pub.voices ?? []) as any[],
        private: (priv.voices ?? []) as any[],
        third_party: ((third as any).voices ?? []) as any[],
      };
    },
    staleTime: STALE,
  });
  const byId = new Map<string, PickerOption>();
  // Order matters: private wins over third_party wins over public on id collision.
  const groups: Array<["public" | "private" | "third_party", any[]]> = [
    ["public", q.data?.public ?? []],
    ["third_party", q.data?.third_party ?? []],
    ["private", q.data?.private ?? []],
  ];
  for (const [source, list] of groups) {
    for (const v of list) {
      const opt = mapVoice(v);
      if (!opt) continue;
      byId.set(opt.id, { ...opt, source });
    }
  }
  const options = Array.from(byId.values());
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}
